import { Timestamp } from 'firebase/firestore';

// Payments v2 statuses plus the legacy values older bubbles may still carry.
// Never decode a raw status directly: use normalizePaymentStatus().
export enum PaymentStatus {
  OPEN = 'open',
  PROCESSING = 'processing',
  PAID = 'paid',
  CANCELLED = 'cancelled',
  PARTIALLY_REFUNDED = 'partially_refunded',
  REFUNDED = 'refunded',
  DISPUTED = 'disputed',
  DISPUTE_LOST = 'dispute_lost',
  UNKNOWN = 'unknown',
  // Legacy (pre-v2); normalizePaymentStatus maps these onto OPEN
  PENDING = 'pending',
  FAILED = 'failed',
  REQUIRES_PAYMENT_METHOD = 'requires_payment_method'
}

/** Unknown-safe decode with the legacy mapping: pending / requires_payment_method / failed -> open.
 *  A status this client has never heard of becomes UNKNOWN (rendered neutrally), never an error. */
export const normalizePaymentStatus = (raw: unknown): PaymentStatus => {
  switch (raw) {
    case undefined:
    case null:
    case '':
    case 'pending':
    case 'requires_payment_method':
    case 'requires_action':
    case 'requires_confirmation':
    case 'failed':
    case 'open':
      return PaymentStatus.OPEN;
    case 'processing': return PaymentStatus.PROCESSING;
    case 'paid':
    case 'succeeded': return PaymentStatus.PAID;
    case 'cancelled':
    case 'canceled': return PaymentStatus.CANCELLED;
    case 'partially_refunded': return PaymentStatus.PARTIALLY_REFUNDED;
    case 'refunded': return PaymentStatus.REFUNDED;
    case 'disputed': return PaymentStatus.DISPUTED;
    case 'dispute_lost': return PaymentStatus.DISPUTE_LOST;
    default: return PaymentStatus.UNKNOWN;
  }
};

export enum MessageType {
  TEXT = 'text',
  IMAGE = 'image', 
  PAYMENT = 'payment',
  PAYMENT_PAID = 'payment_paid',
  PAYMENT_REFUNDED = 'payment_refunded'
}

export interface PaymentData {
  amount: number;
  currency: string;
  description?: string;
  /** Payments v2: what this request is for (Deposit, Balance, ...). */
  label?: string;
  // Support both field name variations (iOS uses snake_case, React expects camelCase)
  paymentIntentId?: string;
  payment_intent_id?: string;
  clientSecret?: string;
  client_secret?: string;
  status: PaymentStatus;
  createdAt: Timestamp;
  created_at?: string;
  enable_card_saving?: boolean;
}

export interface Message {
  id: string;
  messageText: string;
  fromId: string;
  toId: string;
  channelUid: string;
  timestamp?: Timestamp;
  response?: string;
  formQuestions?: string;
  messageType?: MessageType;
  paymentData?: PaymentData;
  /** Storage download URL for an image-question photo attached to this
      message (spec §4b of the Image Questions contract). */
  imageMessageUrl?: string;
  /** Present when this agent message is asking the customer to answer a
      Choice question — quick-reply chips render under the message (Choice
      Questions contract §4). Only meaningful on the MOST RECENT agent
      message, and only until the customer sends their next message. */
  /** response === 'transfer': the agent the customer is handed to. */
  transferToFormId?: string;
  transferToTitle?: string;
  transferToImageUrl?: string;
  choicePrompt?: {
    field: string;
    options: string[];
    multi: boolean;
    allowOther: boolean;
  };
}
